/**
 * Standardized Error Types for Wealthica2 Provider
 * 
 * Provides consistent error handling patterns across all Wealthica2 operations
 */

// Error codes for consistent categorization
export const WEALTHICA_ERROR_CODES = {
  // Authentication errors
  AUTH_FAILED: 'AUTH_FAILED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  
  // Network and API errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  API_ERROR: 'API_ERROR',
  RATE_LIMIT: 'RATE_LIMIT',
  
  // Data validation errors
  INVALID_DATA: 'INVALID_DATA',
  MISSING_REQUIRED: 'MISSING_REQUIRED',
  
  // Connection errors
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  CSRF_MISMATCH: 'CSRF_MISMATCH',
  
  // User-facing errors
  USER_ERROR: 'USER_ERROR',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  
  // System errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export type WealthicaErrorCode = typeof WEALTHICA_ERROR_CODES[keyof typeof WEALTHICA_ERROR_CODES];

/**
 * Standard result format for service layer operations
 */
export interface WealthicaServiceResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: WealthicaErrorCode;
  context?: Record<string, any>;
}

/**
 * Enhanced error class for React Error Boundary integration
 */
export class WealthicaError extends Error {
  public readonly code: WealthicaErrorCode;
  public readonly recoverable: boolean;
  public readonly context?: Record<string, any>;
  public readonly timestamp: number;

  constructor(
    message: string,
    code: WealthicaErrorCode = WEALTHICA_ERROR_CODES.UNKNOWN_ERROR,
    recoverable: boolean = true,
    context?: Record<string, any>
  ) {
    super(message);
    this.name = 'WealthicaError';
    this.code = code;
    this.recoverable = recoverable;
    this.context = context;
    this.timestamp = Date.now();
  }
}

/**
 * Error normalization utility for consistent error handling
 */
export function normalizeWealthicaError(error: unknown): WealthicaServiceResult<never> {
  // Already a WealthicaError
  if (error instanceof WealthicaError) {
    return {
      success: false,
      error: error.message,
      code: error.code,
      context: error.context
    };
  }
  
  // Standard Error object
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Categorize based on error message
    if (message.includes('token') || message.includes('auth') || message.includes('unauthorized')) {
      return {
        success: false,
        error: error.message,
        code: WEALTHICA_ERROR_CODES.AUTH_FAILED
      };
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return {
        success: false,
        error: error.message,
        code: WEALTHICA_ERROR_CODES.NETWORK_ERROR
      };
    }
    
    if (message.includes('rate limit') || message.includes('too many requests')) {
      return {
        success: false,
        error: error.message,
        code: WEALTHICA_ERROR_CODES.RATE_LIMIT
      };
    }
    
    return {
      success: false,
      error: error.message,
      code: WEALTHICA_ERROR_CODES.API_ERROR
    };
  }
  
  // String error
  if (typeof error === 'string') {
    return {
      success: false,
      error,
      code: WEALTHICA_ERROR_CODES.UNKNOWN_ERROR
    };
  }
  
  // Unknown error format
  return {
    success: false,
    error: 'An unknown error occurred',
    code: WEALTHICA_ERROR_CODES.UNKNOWN_ERROR
  };
}

/**
 * Create standardized service result
 */
export function createServiceResult<T>(
  success: boolean,
  data?: T,
  error?: string,
  code?: WealthicaErrorCode,
  context?: Record<string, any>
): WealthicaServiceResult<T> {
  return {
    success,
    data,
    error,
    code,
    context
  };
}

/**
 * Create error result from exception
 */
export function createErrorResult(error: unknown): WealthicaServiceResult<never> {
  return normalizeWealthicaError(error);
}